import React, { useEffect, useState } from "react";
import { TextField, Box, Typography, CircularProgress } from "@mui/material";
import {
  AddPriceDiscountRange,
  EditPriceDiscountRange,
  GetPriceGrouprange,
} from "../../../../Redux/Slices/Sessions/Session.action";
import { useDispatch, useSelector } from "react-redux";
import ActionButton from "../../../../Components/Popups/PopupComponent/ActionButton";
import { toast } from "react-toastify";

let inputstyle = {
  width: "100%",
  borderRadius: "10px",
};

const ManagePriceRange = ({ modalcontrols, data }) => {
  const dispatch = useDispatch();
  const Rangedata = useSelector((store) => store?.Sessions);

  const [maxPrice, setMaxPrice] = useState(data?.max_amount || "");
  const [minPrice, setMinPrice] = useState(0);
  const [discount, setDiscount] = useState(data?.discount || "");
  const [rangeError, setRangeError] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [nextMin, setNextMin] = useState(null);

  // Fetch price range data
  useEffect(() => {
    dispatch(GetPriceGrouprange());
  }, [dispatch]);

  // Set minPrice dynamically and check next range min
  useEffect(() => {
    if (Rangedata?.PriceRange?.length > 0) {
      const allRanges = Rangedata.PriceRange;

      if (data) {
        const currentIndex = allRanges.findIndex((r) => r.id === data.id);

        if (currentIndex !== -1) {
          const nextRange = allRanges[currentIndex + 1];

          // Set min amount based on the existing range
          setMinPrice(data.min_amount);
          setNextMin(nextRange ? nextRange.min_amount : null);
        }
      } else {
        // If adding new range, set minPrice as the last max + 1
        const lastRange = allRanges[allRanges.length - 1];
        setMinPrice(lastRange ? lastRange.max_amount + 1 : 0);
      }
    } else {
      setMinPrice(0);
    }
  }, [Rangedata?.PriceRange, data]);

  // Handle Max Price Input
  const handleMaxPriceInput = (e) => {
    if (e.target.value < 0) return;

    const valueInt = e.target.value !== "" ? parseInt(e.target.value) : "";

    if (valueInt < minPrice + 1) {
      setRangeError(`Max amount must be at least ${minPrice + 1}.`);
    } else if (nextMin !== null && valueInt >= nextMin) {
      setRangeError(`Max amount must be less than ${nextMin}.`);
    } else {
      setRangeError("");
    }

    setMaxPrice(valueInt);
  };

  // Handle Discount Input
  const handleDiscountInput = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 100) {
      setDiscount(value);
    } else {
      setDiscount("");
    }
  };

  // Update button state
  useEffect(() => {
    const isFormValid = maxPrice !== "" && discount !== "" && rangeError === "";
    setIsButtonDisabled(!isFormValid);
  }, [maxPrice, discount, rangeError]);

  // Handle Add/Edit Price
  const handleAddPrice = () => {
    const discountData = new FormData();

    discountData.append("max_amount", maxPrice);
    discountData.append("discount", discount);
    if (data) {
      discountData.append("discount_id", data?.id);
    }

    dispatch(
      data
        ? EditPriceDiscountRange(discountData)
        : AddPriceDiscountRange(discountData)
    )
      .unwrap()
      .then((result) => {
        if (result?.s == 1) {
          toast.success(
            data ? "Discount range updated" : "Discount range added"
          );
          modalcontrols?.closeeditpopup();
        } else {
          toast.error(result?.m);
        }
      });
  };

  return (
    <Box
      className="wrapper"
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: 2,
        p: 3,
        boxShadow: 2,
      }}
    >
      {/* Min Amount Display */}
      {data ? (
        <Typography variant="h6" sx={{ mb: 2, color: "black" }}>
          You can update Max Amount between: {minPrice + 1} -{" "}
          {nextMin ? nextMin - 1 : "∞"}
        </Typography>
      ) : (
        <Typography variant="h6" sx={{ mb: 2, color: "black" }}>
          Min Amount: {minPrice}
        </Typography>
      )}

      {/* Max Price Input */}
      <Box className="price-input" sx={{ display: "flex", my: 2 }}>
        <TextField
          label="Max Amount"
          type="number"
          value={maxPrice}
          onChange={handleMaxPriceInput}
          variant="outlined"
          sx={{
            ...inputstyle,
            "& .MuiOutlinedInput-notchedOutline ": {
              border: "1px solid #F68582",
            },
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
              display: "none",
            },
          }}
          error={!!rangeError}
          helperText={rangeError}
          onKeyDown={(evt) =>
            ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()
          }
          inputProps={{ min: minPrice + 1, max: nextMin ? nextMin - 1 : undefined }}
        />
      </Box>

      {/* Discount Input */}
      <Box sx={{ my: 2 }}>
        <TextField
          label="Discount (%)"
          type="number"
          value={discount}
          onChange={handleDiscountInput}
          variant="outlined"
          inputProps={{ min: 0, max: 100 }}
          onKeyDown={(evt) =>
            ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()
          }
          sx={{
            ...inputstyle,
            "& .MuiOutlinedInput-notchedOutline ": {
              border: "1px solid #F68582",
            },
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
              display: "none",
            },
          }}
        />
      </Box>

      {/* Add Price Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "40%",
          m: "auto",
          mt: "20px",
          gap: "10px",
        }}
      >
        <ActionButton
          title={
            Rangedata?.loading ? (
              <CircularProgress sx={{ color: "#fff" }} />
            ) : data ? (
              "Edit"
            ) : (
              "Add"
            )
          }
          onclick={handleAddPrice}
          disabled={isButtonDisabled || Rangedata?.loading}
        />
      </Box>
    </Box>
  );
};

export default ManagePriceRange;
